/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Formik, yupToFormErrors } from 'formik'
import ValidationSchema from './EditFormValidation'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import Field from '../../../../components/shared/Field'
import Modal from '../../../../components/Modal'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'
import { Accordion, AccordionSection } from '@mdsol/lego/lib/Accordion'
import '@mdsol/lego/lib/Accordion.css'
import './accordion.d.ts'

import AuthorizationStepForm from './steps/AuthorizationStepForm'
import EditStepForm from './steps/EditStepForm'
import ReviewStepForm from './steps/ReviewStepForm'
import ApprovalStepForm from './steps/ApprovalStepForm'
import ReadAcknowledgementStepForm from './steps/ReadAcknowledgementStepForm'
import RejectionReviewStepForm from './steps/RejectionReviewStepForm'

const HalfWidthDiv = styled.div`
  width: 50%;
  float: left;
  padding-right: 15px;
  box-sizing: border-box;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
`

const AccordionWrapper = styled.div`
  margin-top: 20px;

  .accordion-section {
    border: 1px solid #ddd;
    margin-bottom: 10px;
    border-radius: 4px;

    .accordion-section-header {
      background-color: #f5f5f5;
      padding: 12px 15px;
      cursor: pointer;
      font-weight: 600;
      border-bottom: 1px solid #ddd;

      &:hover {
        background-color: #e9e9e9;
      }

      .label-container {
        display: flex;
        align-items: center;

        .label-text {
          flex: 1;
        }
      }
    }

    .accordion-section-body {
      padding: 15px;
      background-color: #fff;
    }

    &.collapsed {
      .accordion-section-body {
        display: none;
      }
    }
  }
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  initialValues: any
  loading?: boolean
  dataLoaded?: boolean // New prop to track if data is fully loaded
}

const EditForm: React.FC<EditFormProps> = ({
  onSubmit,
  doClose,
  isEdit,
  initialValues,
  loading,
  dataLoaded = false
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  // Workflow steps state with collapse management
  const [wfSteps, setWfSteps] = useState({
    authorization: {
      id: 'fb61c973-d3d7-4615-9aa6-ead8732a5583',
      title: 'CHANGE_REQUEST_TYPE_EDIT_PHASE_AUTHORISATION_TITLE',
      collapsed: false
    },
    edit: {
      id: '8f712d46-5218-456d-bbf0-754e6884e1b1',
      title: 'CHANGE_REQUEST_TYPE_EDIT_PHASE_EDIT_TITLE',
      collapsed: true
    },
    review: {
      id: '083ad8a9-e0e1-4552-9cd0-3312f5e0dfaf',
      title: 'CHANGE_REQUEST_TYPE_EDIT_PHASE_REVIEW_TITLE',
      collapsed: true
    },
    approval: {
      id: 'd77ab5e9-0855-453b-8360-7cefc1565961',
      title: 'CHANGE_REQUEST_TYPE_EDIT_PHASE_APPROVAL_TITLE',
      collapsed: true
    },
    readAcknowledgement: {
      id: '2cf606ba-25e4-4408-834e-a52df794016b',
      title: 'CHANGE_REQUEST_TYPE_EDIT_PHASE_ACKNOWLEDGE_TITLE',
      collapsed: true
    },
    rejectionReview: {
      id: '1b1a71b4-8576-4227-94a8-8016ace595cc',
      title: 'CHANGE_REQUEST_TYPE_EDIT_PHASE_REJECTION_TITLE',
      collapsed: true
    }
  })

  const handleWfPhaseToggle = (key: string) => {
    setWfSteps((prevState) => ({
      ...prevState,
      [key]: {
        ...prevState[key as keyof typeof prevState],
        collapsed: !prevState[key as keyof typeof prevState].collapsed
      }
    }))
  }

  return (
    <Loader blocking={loading || false}>
      <Formik
        validate={(values) => {
          const validationSchema = ValidationSchema()
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values, actions) => {
          // Pass values directly - transformation is handled in ModalEdit
          return onSubmit(values, actions)
        }}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <HalfWidthDiv>
                <StyledFormGroup controlId="5f2e5dfd-34c9-4220-8775-17f7d54d91b6" className="w100p">
                  <Form.Label>{translate('NAME')}</Form.Label>
                  <Field type="text" name="name" maxLength={255} autoFocus id="5f2e5dfd-34c9-4220-8775-17f7d54d91b6" />
                  <ErrorMessageStyled name="name" />
                </StyledFormGroup>

                <StyledFormGroup controlId="f1db725e-91c7-4629-8568-f09436af9f31" className="w100p">
                  <Form.Label>{translate('DESCRIPTION')}</Form.Label>
                  <Field type="text" name="description" maxLength={255} id="f1db725e-91c7-4629-8568-f09436af9f31" />
                  <ErrorMessageStyled name="description" />
                </StyledFormGroup>
              </HalfWidthDiv>
              <div style={{ clear: 'both' }}></div>
              {/* Workflow Steps Accordion */}
              <Accordion>
                {/* Authorization Step */}
                <AccordionSection
                  key="authorization"
                  headerContent={
                    <div className="label-container">
                      <div className="label-text" id={wfSteps.authorization.id}>
                        {translate(wfSteps.authorization.title)}
                      </div>
                    </div>
                  }
                  eventKey="authorization"
                  collapsed={wfSteps.authorization.collapsed}
                  onSectionToggle={() => handleWfPhaseToggle('authorization')}
                >
                  {!dataLoaded && isEdit ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>Loading data...</div>
                    </div>
                  ) : (
                    <AuthorizationStepForm values={values} setFieldValue={setFieldValue} />
                  )}
                </AccordionSection>

                {/* Edit Step */}
                <AccordionSection
                  key="edit"
                  headerContent={
                    <div className="label-container">
                      <div className="label-text" id={wfSteps.edit.id}>
                        {translate(wfSteps.edit.title)}
                      </div>
                    </div>
                  }
                  eventKey="edit"
                  collapsed={wfSteps.edit.collapsed}
                  onSectionToggle={() => handleWfPhaseToggle('edit')}
                >
                  {!dataLoaded && isEdit ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>Loading data...</div>
                    </div>
                  ) : (
                    <EditStepForm values={values} setFieldValue={setFieldValue} />
                  )}
                </AccordionSection>

                {/* Review Step */}
                <AccordionSection
                  key="review"
                  headerContent={
                    <div className="label-container">
                      <div className="label-text" id={wfSteps.review.id}>
                        {translate(wfSteps.review.title)}
                      </div>
                    </div>
                  }
                  eventKey="review"
                  collapsed={wfSteps.review.collapsed}
                  onSectionToggle={() => handleWfPhaseToggle('review')}
                >
                  {!dataLoaded && isEdit ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>Loading data...</div>
                    </div>
                  ) : (
                    <ReviewStepForm values={values} setFieldValue={setFieldValue} />
                  )}
                </AccordionSection>

                {/* Approval Step */}
                <AccordionSection
                  key="approval"
                  headerContent={
                    <div className="label-container">
                      <div className="label-text" id={wfSteps.approval.id}>
                        {translate(wfSteps.approval.title)}
                      </div>
                    </div>
                  }
                  eventKey="approval"
                  collapsed={wfSteps.approval.collapsed}
                  onSectionToggle={() => handleWfPhaseToggle('approval')}
                >
                  {!dataLoaded && isEdit ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>Loading data...</div>
                    </div>
                  ) : (
                    <ApprovalStepForm values={values} setFieldValue={setFieldValue} />
                  )}
                </AccordionSection>

                {/* Read Acknowledgement Step */}
                <AccordionSection
                  key="readAcknowledgement"
                  headerContent={
                    <div className="label-container">
                      <div className="label-text" id={wfSteps.readAcknowledgement.id}>
                        {translate(wfSteps.readAcknowledgement.title)}
                      </div>
                    </div>
                  }
                  eventKey="readAcknowledgement"
                  collapsed={wfSteps.readAcknowledgement.collapsed}
                  onSectionToggle={() => handleWfPhaseToggle('readAcknowledgement')}
                >
                  {!dataLoaded && isEdit ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>Loading data...</div>
                    </div>
                  ) : (
                    <ReadAcknowledgementStepForm values={values} setFieldValue={setFieldValue} />
                  )}
                </AccordionSection>

                {/* Rejection Review Step */}
                <AccordionSection
                  key="rejectionReview"
                  headerContent={
                    <div className="label-container">
                      <div className="label-text" id={wfSteps.rejectionReview.id}>
                        {translate(wfSteps.rejectionReview.title)}
                      </div>
                    </div>
                  }
                  eventKey="rejectionReview"
                  collapsed={wfSteps.rejectionReview.collapsed}
                  onSectionToggle={() => handleWfPhaseToggle('rejectionReview')}
                >
                  {!dataLoaded && isEdit ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div style={{ marginTop: '10px', color: '#666' }}>Loading data...</div>
                    </div>
                  ) : (
                    <RejectionReviewStepForm values={values} setFieldValue={setFieldValue} />
                  )}
                </AccordionSection>
              </Accordion>
            </Modal.Body>

            <Modal.Footer>
              <button
                type="button"
                className="btn btn-link"
                onClick={doClose}
                disabled={isSubmitting}
                id="e0e98099-284d-4cb5-b7ce-e797a0e558a8"
              >
                {translate('CANCEL')}
              </button>
              <button
                type="submit"
                className="btn btn-primary text-capitalize"
                disabled={isSubmitting}
                id="af8140ae-93b9-4dce-8565-b0ace0ad6589"
              >
                {isSubmitting ? translate('SAVING') : translate('SAVE')}
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Loader>
  )
}

export default EditForm
